/**
 * Session Storage Backend
 * Ported from: innovation-claude/packages/storage/src/backends/session.rs
 *
 * Uses browser sessionStorage for session-scoped storage (cleared when tab closes)
 */

import { StorageError } from '../StorageError'

/**
 * SessionBackend - Uses browser sessionStorage
 *
 * Storage characteristics:
 * - Session-scoped (cleared when tab/window closes)
 * - ~5-10MB quota per origin
 * - Synchronous API (but we wrap in async for interface compatibility)
 * - NOT shared across tabs (each tab has own sessionStorage)
 *
 * Use cases:
 * - Authentication tokens (tradovate-auth)
 * - Session preferences (trading environment)
 * - Temporary state (wizard progress)
 */
export class SessionBackend {
    /**
     * Save data to sessionStorage
     */
    async save(key: string, json: string): Promise<void> {
        try {
            sessionStorage.setItem(key, json)
        } catch (error) {
            if (error instanceof Error && error.name === 'QuotaExceededError') {
                throw StorageError.quotaExceeded(`sessionStorage quota exceeded for key: ${key}`)
            }
            throw StorageError.platform(`Failed to save to sessionStorage: ${error}`)
        }
    }

    /**
     * Load data from sessionStorage
     */
    async load(key: string): Promise<string | null> {
        try {
            return sessionStorage.getItem(key)
        } catch (error) {
            throw StorageError.platform(`Failed to load from sessionStorage: ${error}`)
        }
    }

    /**
     * Delete data from sessionStorage
     */
    async delete(key: string): Promise<void> {
        try {
            sessionStorage.removeItem(key)
        } catch (error) {
            throw StorageError.platform(`Failed to delete from sessionStorage: ${error}`)
        }
    }

    /**
     * Check if sessionStorage is available
     */
    static isAvailable(): boolean {
        try {
            const test = '__storage_test__'
            sessionStorage.setItem(test, test)
            sessionStorage.removeItem(test)
            return true
        } catch {
            return false
        }
    }
}
